import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';

type Lohnklasse =
  | 'festanstellung'
  | 'teilzeit'
  | 'minijob'
  | 'freelance'
  | 'auszubildender'
  | 'praktikant'
  | 'sonstiges';

interface Mitarbeiter {
  id: string;
  personalnummer: string | null;
  anrede: string | null;
  vorname: string;
  nachname: string;
  email: string | null;
  telefon: string | null;
  mobil: string | null;
  adresseStrasse: string | null;
  adressePlz: string | null;
  adresseOrt: string | null;
  adresseLand: string;
  geburtsdatum: string | null;
  eintrittsdatum: string;
  austrittsdatum: string | null;
  position: string | null;
  abteilung: Abteilung;
  lohnklasse: Lohnklasse | null;
  stundensatzIntern: string | null;
  userId: string | null;
  aktiv: boolean;
  notizen: string | null;
  createdAt: string;
  updatedAt: string;
}

type Abteilung = 'haustechnik' | 'reinigung' | 'gaertner' | 'buero' | 'leitung';

const ABTEILUNGEN: { value: Abteilung; label: string }[] = [
  { value: 'haustechnik', label: 'Haustechnik' },
  { value: 'reinigung', label: 'Reinigung' },
  { value: 'gaertner', label: 'Gartenbau' },
  { value: 'buero', label: 'Buero' },
  { value: 'leitung', label: 'Leitung' },
];

interface User {
  id: string;
  email: string;
  displayName: string;
  roleName: string | null;
}

const LOHN_VALUES: Lohnklasse[] = [
  'festanstellung',
  'teilzeit',
  'minijob',
  'freelance',
  'auszubildender',
  'praktikant',
  'sonstiges',
];

export const Route = createFileRoute('/mitarbeiter/$id')({
  beforeLoad: () => requireAccess('/api/v1/mitarbeiter'),
  component: MitarbeiterDetailPage,
});

function MitarbeiterDetailPage() {
  const { id } = Route.useParams();
  const mQ = useQuery({
    queryKey: ['mitarbeiter', id],
    queryFn: async () => {
      try {
        return await api<{ item: Mitarbeiter }>(`/api/v1/mitarbeiter/${id}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
  const userQ = useQuery({
    queryKey: ['user', mQ.data?.item.userId],
    queryFn: () => api<{ item: User }>(`/api/v1/users/${mQ.data!.item.userId}`),
    enabled: !!mQ.data?.item.userId,
  });

  if (mQ.isLoading) return <div className="text-slate-500">Lade…</div>;
  if (mQ.error) return <ErrorBanner error={mQ.error} />;
  if (!mQ.data) {
    return (
      <div className="space-y-4">
        <Link to="/mitarbeiter" className="text-sm text-brand-700 hover:underline">
          ← zurück zur Mitarbeiterliste
        </Link>
        <div className="rounded-md border border-slate-200 bg-white p-6 text-slate-500">
          Mitarbeiter nicht gefunden.
        </div>
      </div>
    );
  }
  const m = mQ.data.item;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/mitarbeiter" className="text-sm text-brand-700 hover:underline">
          ← zurück zur Mitarbeiterliste
        </Link>
      </div>
      <PageHeader
        title={`${m.vorname} ${m.nachname}`}
        description={
          m.personalnummer
            ? `Pers-Nr. ${m.personalnummer}${m.position ? ` · ${m.position}` : ''}`
            : (m.position ?? 'Mitarbeiter')
        }
      />

      <MitarbeiterStammdaten mitarbeiter={m} user={userQ.data?.item ?? null} />
    </div>
  );
}

function MitarbeiterStammdaten({
  mitarbeiter: m,
  user,
}: {
  mitarbeiter: Mitarbeiter;
  user: User | null;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    position: m.position ?? '',
    abteilung: m.abteilung,
    lohnklasse: (m.lohnklasse ?? '') as '' | Lohnklasse,
    stundensatzIntern: m.stundensatzIntern ?? '',
    email: m.email ?? '',
    telefon: m.telefon ?? '',
    mobil: m.mobil ?? '',
    adresseStrasse: m.adresseStrasse ?? '',
    adressePlz: m.adressePlz ?? '',
    adresseOrt: m.adresseOrt ?? '',
    austrittsdatum: m.austrittsdatum ?? '',
    aktiv: m.aktiv,
    notizen: m.notizen ?? '',
  });
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: async () => {
      const patch: Record<string, unknown> = {};
      const addIfChanged = (key: keyof typeof form, current: string | null) => {
        if (form[key] !== (current ?? '')) {
          patch[key] = form[key] === '' ? (key === 'austrittsdatum' ? null : '') : form[key];
        }
      };
      addIfChanged('position', m.position);
      addIfChanged('email', m.email);
      addIfChanged('telefon', m.telefon);
      addIfChanged('mobil', m.mobil);
      addIfChanged('adresseStrasse', m.adresseStrasse);
      addIfChanged('adressePlz', m.adressePlz);
      addIfChanged('adresseOrt', m.adresseOrt);
      addIfChanged('notizen', m.notizen);
      if (form.austrittsdatum !== (m.austrittsdatum ?? '')) {
        patch['austrittsdatum'] = form.austrittsdatum === '' ? null : form.austrittsdatum;
      }
      if (form.lohnklasse !== (m.lohnklasse ?? '')) {
        patch['lohnklasse'] = form.lohnklasse || null;
      }
      if (form.stundensatzIntern !== (m.stundensatzIntern ?? '')) {
        patch['stundensatzIntern'] =
          form.stundensatzIntern === '' ? undefined : Number(form.stundensatzIntern);
      }
      if (form.aktiv !== m.aktiv) patch['aktiv'] = form.aktiv;
      if (form.abteilung !== m.abteilung) patch['abteilung'] = form.abteilung;
      if (Object.keys(patch).length === 0) return m;
      return api<{ item: Mitarbeiter }>(`/api/v1/mitarbeiter/${m.id}`, {
        method: 'PATCH',
        body: patch,
      });
    },
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['mitarbeiter', m.id] });
      void qc.invalidateQueries({ queryKey: ['mitarbeiter'] });
    },
    onError: (err) => setError(err),
  });

  return (
    <Panel
      title="Stammdaten"
      actions={
        !editing ? (
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Bearbeiten
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setForm({
                  position: m.position ?? '',
                  abteilung: m.abteilung,
                  lohnklasse: (m.lohnklasse ?? '') as '' | Lohnklasse,
                  stundensatzIntern: m.stundensatzIntern ?? '',
                  email: m.email ?? '',
                  telefon: m.telefon ?? '',
                  mobil: m.mobil ?? '',
                  adresseStrasse: m.adresseStrasse ?? '',
                  adressePlz: m.adressePlz ?? '',
                  adresseOrt: m.adresseOrt ?? '',
                  austrittsdatum: m.austrittsdatum ?? '',
                  aktiv: m.aktiv,
                  notizen: m.notizen ?? '',
                });
                setError(null);
                setEditing(false);
              }}
              disabled={save.isPending}
            >
              Abbrechen
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
        )
      }
    >
      <ErrorBanner error={error} />
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Field label="Pers-Nr.">
          <span className="font-mono text-slate-600">{m.personalnummer ?? '–'}</span>
        </Field>
        <Field label="Name">
          <span className="font-medium">
            {m.vorname} {m.nachname}
          </span>
        </Field>
        <Field label="Position">
          {editing ? (
            <Input
              value={form.position}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
            />
          ) : (
            <span className="text-slate-600">{m.position ?? '–'}</span>
          )}
        </Field>
        <Field label="Abteilung">
          {editing ? (
            <Select
              value={form.abteilung}
              onChange={(e) => setForm({ ...form, abteilung: e.target.value as Abteilung })}
            >
              {ABTEILUNGEN.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </Select>
          ) : (
            <span className="text-slate-600">
              {ABTEILUNGEN.find((a) => a.value === m.abteilung)?.label ?? m.abteilung}
            </span>
          )}
        </Field>
        <Field label="Lohnklasse">
          {editing ? (
            <Select
              value={form.lohnklasse}
              onChange={(e) => setForm({ ...form, lohnklasse: e.target.value as Lohnklasse | '' })}
            >
              <option value="">–</option>
              {LOHN_VALUES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Select>
          ) : (
            <span className="text-slate-600">{m.lohnklasse ?? '–'}</span>
          )}
        </Field>
        <Field label="Stundensatz intern (€)">
          {editing ? (
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.stundensatzIntern}
              onChange={(e) => setForm({ ...form, stundensatzIntern: e.target.value })}
            />
          ) : (
            <span className="text-slate-600 tabular-nums">
              {m.stundensatzIntern
                ? `${Number(m.stundensatzIntern).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
                : '–'}
            </span>
          )}
        </Field>
        <Field label="Status">
          {editing ? (
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.aktiv}
                onChange={(e) => setForm({ ...form, aktiv: e.target.checked })}
              />
              aktiv
            </label>
          ) : (
            <span
              className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${
                m.aktiv
                  ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                  : 'bg-slate-100 text-slate-500 border-slate-200'
              }`}
            >
              {m.aktiv ? 'aktiv' : 'inaktiv'}
            </span>
          )}
        </Field>
        <Field label="E-Mail">
          {editing ? (
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          ) : (
            <span className="text-slate-600">
              {m.email ? (
                <a href={`mailto:${m.email}`} className="hover:underline">
                  {m.email}
                </a>
              ) : (
                '–'
              )}
            </span>
          )}
        </Field>
        <Field label="Telefon">
          {editing ? (
            <Input
              value={form.telefon}
              onChange={(e) => setForm({ ...form, telefon: e.target.value })}
            />
          ) : (
            <span className="text-slate-600">{m.telefon ?? '–'}</span>
          )}
        </Field>
        <Field label="Mobil">
          {editing ? (
            <Input
              value={form.mobil}
              onChange={(e) => setForm({ ...form, mobil: e.target.value })}
            />
          ) : (
            <span className="text-slate-600">{m.mobil ?? '–'}</span>
          )}
        </Field>
        <Field label="Auth-User-Verknuepfung">
          {user ? (
            <Link to="/users" className="text-brand-700 hover:underline">
              {user.email} ({user.roleName ?? '–'})
            </Link>
          ) : (
            <span className="text-slate-400">nicht verknuepft</span>
          )}
        </Field>
        <Field label="Eintritt / Austritt">
          {editing ? (
            <div className="flex gap-2 items-center">
              <span className="text-slate-500">{m.eintrittsdatum}</span>
              <span className="text-slate-400">→</span>
              <Input
                type="date"
                value={form.austrittsdatum}
                onChange={(e) => setForm({ ...form, austrittsdatum: e.target.value })}
              />
            </div>
          ) : (
            <span className="text-slate-600">
              {m.eintrittsdatum}
              {m.austrittsdatum ? ` → ${m.austrittsdatum}` : ''}
            </span>
          )}
        </Field>
        <Field label="Strasse">
          {editing ? (
            <Input
              value={form.adresseStrasse}
              onChange={(e) => setForm({ ...form, adresseStrasse: e.target.value })}
            />
          ) : (
            <span className="text-slate-600">{m.adresseStrasse ?? '–'}</span>
          )}
        </Field>
        <Field label="PLZ / Ort">
          {editing ? (
            <div className="flex gap-2">
              <Input
                className="w-24"
                value={form.adressePlz}
                onChange={(e) => setForm({ ...form, adressePlz: e.target.value })}
              />
              <Input
                value={form.adresseOrt}
                onChange={(e) => setForm({ ...form, adresseOrt: e.target.value })}
              />
            </div>
          ) : (
            <span className="text-slate-600">
              {[m.adressePlz, m.adresseOrt].filter(Boolean).join(' ') || '–'}
            </span>
          )}
        </Field>
        <div className="md:col-span-2">
          <Field label="Notizen">
            {editing ? (
              <textarea
                value={form.notizen}
                onChange={(e) => setForm({ ...form, notizen: e.target.value })}
                rows={3}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            ) : (
              <span className="text-slate-600 whitespace-pre-wrap">{m.notizen ?? '–'}</span>
            )}
          </Field>
        </div>
      </dl>
    </Panel>
  );
}

function Panel({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {actions}
      </header>
      <div className="p-4 space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}
